function moneyLabel(cents, prefix) {
  const amount = (Number(cents || 0) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${prefix}${amount}`;
}

function parseJson(value, fallback) {
  try {
    return JSON.parse(value || '');
  } catch {
    return fallback;
  }
}

/*
 * Description: clamp to N lines with a Read more toggle, only when it is longer than that.
 * Returns a cleanup function.
 */
function bootDescription(root) {
  const wrap = root.querySelector('[data-desc]');
  const body = wrap?.querySelector('[data-desc-body]');
  const toggle = wrap?.querySelector('[data-desc-toggle]');
  const label = toggle?.querySelector('[data-desc-label]');
  if (!wrap || !body || !toggle) return () => {};

  let expanded = false;

  const measure = () => {
    if (expanded) return;
    // Measure the full height, then compare with the clamped height.
    wrap.classList.remove('is-clamped');
    body.style.maxHeight = '';
    const full = body.scrollHeight;
    wrap.classList.add('is-clamped');
    const clamped = body.clientHeight;
    const needsToggle = full > clamped + 4;
    toggle.hidden = !needsToggle;
    if (!needsToggle) wrap.classList.remove('is-clamped');
  };

  const onToggle = () => {
    expanded = !expanded;
    if (expanded) {
      body.style.maxHeight = `${body.scrollHeight}px`;
      wrap.classList.remove('is-clamped');
      // Let the height animate, then drop the limit so later layout changes are not clipped.
      window.setTimeout(() => {
        if (expanded) body.style.maxHeight = 'none';
      }, 480);
    } else {
      body.style.maxHeight = `${body.scrollHeight}px`;
      requestAnimationFrame(() => {
        wrap.classList.add('is-clamped');
        body.style.maxHeight = '';
      });
    }
    toggle.setAttribute('aria-expanded', String(expanded));
    if (label) label.textContent = expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
  };

  toggle.addEventListener('click', onToggle);
  const ro = window.ResizeObserver ? new ResizeObserver(() => measure()) : null;
  ro?.observe(wrap);
  measure();

  return () => {
    toggle.removeEventListener('click', onToggle);
    ro?.disconnect();
  };
}

function bootProduct(root) {
  if (!root || root.dataset.booted === '1') return;
  root.dataset.booted = '1';
  const cleanupDescription = bootDescription(root);

  const prefix = root.getAttribute('data-money-prefix') || 'Rs.';
  const productNode = root.querySelector('[data-product-json]');
  const product = parseJson(productNode?.textContent, {});
  const variants = product.variants || [];
  const media = product.media || [];
  const options = product.options || [];
  const slots = (root.getAttribute('data-slots') || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
  const viewMin = Number(root.getAttribute('data-view-min') || 8);
  const viewMax = Number(root.getAttribute('data-view-max') || 24);

  const mainImg = root.querySelector('[data-main-img]');
  const thumbs = root.querySelector('[data-thumbs]');
  const priceEl = root.querySelector('[data-price]');
  const optionEls = [...root.querySelectorAll('[data-option-index]')];
  const optionLabels = [...root.querySelectorAll('[data-option-label]')];
  const addonTabs = root.querySelector('[data-addon-tabs]');
  const qtyInput = root.querySelector('[data-qty]');
  const terms = root.querySelector('[data-terms]');
  const tagSelect = root.querySelector('[data-tag]');
  const dateInput = root.querySelector('[data-date]');
  const slotSelect = root.querySelector('[data-slot]');
  const noteInput = root.querySelector('[data-note]');
  const addBtn = root.querySelector('[data-add]');
  const buyBtn = root.querySelector('[data-buy]');
  const wishBtn = root.querySelector('[data-wish]');
  const zoomBtn = root.querySelector('[data-zoom]');
  const viewersEl = root.querySelector('[data-viewers]');
  const shareBtn = root.querySelector('[data-share]');
  const errorEl = root.querySelector('[data-error]');

  const state = {
    optionValues: options.map((opt) => {
      const selected = product.selected_or_first_available_variant?.options || [];
      const idx = options.indexOf(opt);
      return selected[idx] || opt.values?.[0] || '';
    }),
    variant: null,
    mediaIndex: 0,
    qty: 1,
  };

  function findVariant() {
    return (
      variants.find((v) =>
        (v.options || []).every((val, i) => String(val) === String(state.optionValues[i]))
      ) || null
    );
  }

  function setMainMedia(index) {
    const item = media[index];
    if (!item || !mainImg) return;
    state.mediaIndex = index;
    mainImg.src = item.src;
    mainImg.srcset = item.srcset || '';
    mainImg.alt = item.alt || product.title || '';
    thumbs?.querySelectorAll('[data-thumb]').forEach((btn, i) => {
      btn.setAttribute('aria-pressed', i === index ? 'true' : 'false');
    });
  }

  function mediaForVariant(variant) {
    if (!variant) return 0;
    if (variant.featured_media?.id) {
      const idx = media.findIndex((m) => String(m.id) === String(variant.featured_media.id));
      if (idx >= 0) return idx;
    }
    if (variant.featured_image?.id) {
      const idx = media.findIndex((m) => String(m.id) === String(variant.featured_image.id));
      if (idx >= 0) return idx;
    }
    return state.mediaIndex;
  }

  function renderOptions() {
    optionEls.forEach((group) => {
      const index = Number(group.getAttribute('data-option-index'));
      group.querySelectorAll('[data-option-value]').forEach((btn) => {
        const value = btn.getAttribute('data-option-value');
        const on = String(state.optionValues[index]) === String(value);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        btn.classList.toggle('is-selected', on);
      });
      const label = optionLabels.find((el) => Number(el.getAttribute('data-option-label')) === index);
      if (label) {
        const name = label.getAttribute('data-option-name') || 'Option';
        label.textContent = `${name}: ${state.optionValues[index] || ''}`;
      }
    });
  }

  function updateVariant() {
    state.variant = findVariant();
    renderOptions();
    if (state.variant) {
      if (priceEl) priceEl.textContent = moneyLabel(state.variant.price, prefix);
      setMainMedia(mediaForVariant(state.variant));
      const available = state.variant.available !== false;
      if (addBtn) addBtn.disabled = !available;
      if (buyBtn) buyBtn.disabled = !available;
    } else {
      if (addBtn) addBtn.disabled = true;
      if (buyBtn) buyBtn.disabled = true;
    }
  }

  function selectedAddons() {
    return [...root.querySelectorAll('[data-addon]:checked')].map((input) => ({
      id: input.getAttribute('data-variant'),
      name: input.getAttribute('data-name') || '',
      cents: Number(input.getAttribute('data-cents') || 0),
    }));
  }

  function showError(message) {
    if (!errorEl) return;
    errorEl.hidden = !message;
    errorEl.textContent = message || '';
  }

  function buildProperties() {
    const properties = {};
    if (tagSelect?.value) properties.Tag = tagSelect.value;
    if (dateInput?.value) properties['Delivery Date'] = dateInput.value;
    if (slotSelect?.value) properties.Slot = slotSelect.value;
    if (noteInput?.value?.trim()) properties.Note = noteInput.value.trim();
    const addonNames = selectedAddons()
      .map((a) => a.name)
      .filter(Boolean);
    if (addonNames.length) properties.Addons = addonNames.join(', ');
    return properties;
  }

  function validate() {
    if (!state.variant?.id) {
      showError('Please select an available option.');
      return false;
    }
    if (terms && !terms.checked) {
      showError('Please agree with Terms & Conditions.');
      terms.focus();
      return false;
    }
    showError('');
    return true;
  }

  async function addToCart({ checkout } = {}) {
    if (!validate()) return;
    if (addBtn?.getAttribute('aria-busy') === 'true') return;
    const qty = Math.max(1, Number(qtyInput?.value || state.qty || 1));
    const items = [
      {
        id: Number(state.variant.id),
        quantity: qty,
        properties: buildProperties(),
      },
    ];
    selectedAddons().forEach((addon) => {
      if (addon.id) items.push({ id: Number(addon.id), quantity: 1 });
    });

    const busy = [addBtn, buyBtn].filter(Boolean);
    busy.forEach((btn) => btn.setAttribute('aria-busy', 'true'));

    try {
      const url = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.description || data.message || 'Could not add to cart');
      }
      if (checkout) {
        window.location.href = '/checkout';
        return;
      }
      if (window.ArtinkCart?.openDropdown) {
        await window.ArtinkCart.openDropdown();
      } else if (window.ArtinkCart?.refresh) {
        await window.ArtinkCart.refresh();
      } else {
        const cartRes = await fetch((window.Theme?.routes?.cart_url || '/cart') + '.js');
        if (cartRes.ok) {
          const cart = await cartRes.json();
          document.dispatchEvent(
            new CustomEvent('cart:update', { detail: { cart, itemCount: cart.item_count } })
          );
        }
      }
    } catch (error) {
      showError(error.message || 'Could not add to cart');
      console.warn('[artink-product]', error);
    } finally {
      busy.forEach((btn) => btn.removeAttribute('aria-busy'));
    }
  }

  // Gallery thumbs
  thumbs?.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-thumb]');
    if (!btn) return;
    setMainMedia(Number(btn.getAttribute('data-thumb')));
  });

  zoomBtn?.addEventListener('click', () => {
    const item = media[state.mediaIndex];
    if (!item?.src) return;
    window.open(item.src, '_blank', 'noopener');
  });

  // Variant options
  optionEls.forEach((group) => {
    group.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-option-value]');
      if (!btn) return;
      const index = Number(group.getAttribute('data-option-index'));
      state.optionValues[index] = btn.getAttribute('data-option-value');
      updateVariant();
    });
  });

  // Addon tabs — only one collection panel visible at a time
  function showAddonPanel(key) {
    addonTabs?.querySelectorAll('[data-addon-tab]').forEach((node) => {
      const on = node.getAttribute('data-addon-tab') === key;
      node.setAttribute('aria-selected', on ? 'true' : 'false');
      node.classList.toggle('is-active', on);
    });
    root.querySelectorAll('[data-addon-panel]').forEach((panel) => {
      const on = panel.getAttribute('data-addon-panel') === key;
      panel.hidden = !on;
      panel.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
  }

  addonTabs?.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-addon-tab]');
    if (!tab) return;
    showAddonPanel(tab.getAttribute('data-addon-tab'));
  });

  // Qty
  root.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
    const next = Math.max(1, Number(qtyInput?.value || 1) - 1);
    if (qtyInput) qtyInput.value = String(next);
    state.qty = next;
  });
  root.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
    const next = Math.max(1, Number(qtyInput?.value || 1) + 1);
    if (qtyInput) qtyInput.value = String(next);
    state.qty = next;
  });
  qtyInput?.addEventListener('change', () => {
    const next = Math.max(1, Number(qtyInput.value || 1));
    qtyInput.value = String(next);
    state.qty = next;
  });

  addBtn?.addEventListener('click', () => addToCart({ checkout: false }));
  buyBtn?.addEventListener('click', () => addToCart({ checkout: true }));

  wishBtn?.addEventListener('click', () => {
    const on = wishBtn.getAttribute('aria-pressed') !== 'true';
    wishBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
    wishBtn.classList.toggle('is-on', on);
  });

  shareBtn?.addEventListener('click', async () => {
    const shareData = {
      title: product.title || document.title,
      url: window.location.href,
    };
    try {
      if (navigator.share) await navigator.share(shareData);
      else {
        await navigator.clipboard.writeText(window.location.href);
        shareBtn.dataset.copied = '1';
        setTimeout(() => delete shareBtn.dataset.copied, 1600);
      }
    } catch {
      /* cancelled */
    }
  });

  root.querySelector('[data-compare]')?.addEventListener('click', () => {
    optionEls[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });

  // Slot options
  if (slotSelect && slots.length) {
    slots.forEach((label) => {
      const opt = document.createElement('option');
      opt.value = label;
      opt.textContent = label;
      slotSelect.appendChild(opt);
    });
  }

  // Date min = today
  if (dateInput) {
    const today = new Date();
    dateInput.min = today.toISOString().slice(0, 10);
    const maxDays = Number(root.getAttribute('data-max-days') || 60);
    const max = new Date();
    max.setDate(max.getDate() + maxDays);
    dateInput.max = max.toISOString().slice(0, 10);
  }

  // Viewers count
  if (viewersEl) {
    const span = Math.max(0, viewMax - viewMin);
    const count = viewMin + Math.floor(Math.random() * (span + 1));
    viewersEl.textContent = String(count);
  }

  // Add-ons summary: "2 added · Rs. 4,200", hidden while nothing is ticked.
  const addonSummary = root.querySelector('[data-addon-summary]');
  const renderAddonSummary = () => {
    if (!addonSummary) return;
    const ticked = [...root.querySelectorAll('[data-addon]:checked')];
    if (!ticked.length) {
      addonSummary.hidden = true;
      addonSummary.textContent = '';
      return;
    }
    const cents = ticked.reduce((sum, input) => sum + Number(input.getAttribute('data-cents') || 0), 0);
    const amount = Math.round(cents / 100).toLocaleString('en-US');
    const addedLabel = addonSummary.getAttribute('data-label-added') || 'added';
    addonSummary.textContent = `${ticked.length} ${addedLabel} · ${prefix.trim()} ${amount}`;
    addonSummary.hidden = false;
  };
  root.addEventListener('change', (event) => {
    if (event.target instanceof Element && event.target.matches('[data-addon]')) renderAddonSummary();
  });
  renderAddonSummary();

  // Init selected variant from product
  if (product.selected_or_first_available_variant?.options) {
    state.optionValues = [...product.selected_or_first_available_variant.options];
  }
  updateVariant();
  if (media.length) setMainMedia(mediaForVariant(state.variant) || 0);

  // Activate first addon tab (hide other collection panels)
  const firstTab = addonTabs?.querySelector('[data-addon-tab]');
  if (firstTab) {
    showAddonPanel(firstTab.getAttribute('data-addon-tab'));
  } else {
    root.querySelectorAll('[data-addon-panel]').forEach((panel, index) => {
      panel.hidden = index !== 0;
    });
  }

  root._artinkProductCleanup = () => {
    cleanupDescription();
    root.dataset.booted = '0';
  };
}

function initAll() {
  document.querySelectorAll('[data-artink-product]').forEach(bootProduct);
}

initAll();
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-artink-product]').forEach(bootProduct);
});
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-artink-product]').forEach((root) => {
    root._artinkProductCleanup?.();
  });
});
